import { AxiosResponse } from "axios";
import BaseAPI from "../api";

const path = "zone";

export interface paramsFindAll {
  page: number;
  pageLimit: number;
  branchId?: number;
  search?: string;
  leasesType?: number;
  status?: string;
  sortBy?: string;
  sortType?: "ASC" | "DESC";
  /** 0 = ข้ามการคำนวณ ไม่แนบ meterNumber[].utilities มาด้วย (เร็วกว่ามาก)
   *  ไม่ส่ง หรือส่งค่าอื่น = คำนวณเต็ม */
  skipcalculation?: number;
}

/** พิกัดและขนาด: backend เก็บเป็น DECIMAL จึงคืนมาเป็น string เช่น "454.00"
 *  แต่ค่าที่ฝั่ง client สร้างเอง (ตอนวาดโซนใหม่ / ลากกล่อง) เป็น number
 *  จึงเป็นได้ทั้งสองแบบจริง ๆ — ห้ามบังคับเป็น number อย่างเดียว */
type Dimension = number | string;

export interface IZone {
  zoneId?: number;
  zoneName: string;
  zoneNameEn?: string | null;
  branchId: number;
  x: Dimension;
  y: Dimension;
  width: Dimension;
  height: Dimension;
  realWidth: Dimension;
  realHeight: Dimension;
  color: string;
  status?: string;
  stall: IStall[];

  /** ด้านล่างเป็น flag ฝั่ง client ล้วน ไม่เคยมาจาก API */
  isEdit?: boolean;
  isChange?: boolean;
  vertical?: number | null;
  horizontal?: number | null;
}

export interface IStall {
  stallId?: number | null;
  zoneId?: number | null;
  stallName: string;
  stallNumber: string
  x: Dimension;
  y: Dimension;
  width: Dimension;
  height: Dimension;
  realWidth: Dimension | null;
  realHeight: Dimension | null;
  color: string;
  stallTypeId: number | null;
  status?: "OPEN" | "BLANK" | "RESERVE" | "CONTRACT" | "DELETED" | "NON_INFO";
  meterNumber?: IMeterNumber[];

  /** field ที่ backend ส่งมาแต่เดิมไม่ได้ประกาศไว้ */
  stallType?: IStallType | null;
  leaseAgreementId?: number | null;

  /** flag ฝั่ง client ล้วน */
  noStall?: number;
  isEdit?: boolean;
}

export interface IStallType {
  stallTypeId?: number;
  stallTypeNameTh?: string;
  stallTypeNameEn?: string;
}

export interface IMeterNumber {
  meterId?: number;
  stallId?: number;
  meterNumber: string;
  meterType: "WATER" | "ELECTRICITY";
  /** โครงยังไม่ได้ไล่ครบ ใช้เฉพาะหน้าค่าน้ำ/ค่าไฟ · ไม่มาถ้าส่ง skipcalculation=0 */
  utilities?: any;
}
/**
 * รูปข้อมูลของ `GET zone/lite?view=lookup` — เอาไปทำ dropdown โซน + แผงค้า (cascading)
 *
 * ประกาศคู่ใหม่แทนการ reuse IZone/IStall เพราะสองตัวนั้นบังคับ field ที่ lookup ไม่คืนมา
 * (x/y/width/height/color/branchId/stallTypeId) — assign เป็น IZone[] จะ compile ไม่ผ่าน
 */
export interface IStallLookup {
  stallId: number;
  stallName: string | null;
  stallNumber: string | null;
}

export interface IZoneLookup {
  zoneId: number;
  zoneName: string | null;
  stall: IStallLookup[];
}

export default class ZoneApi extends BaseAPI {
  static findAll(props: paramsFindAll): Promise<any> {
    return this.api.get(path, { params: { ...props } }).then((res) => res);
  }

  static list(query: paramsFindAll): Promise<AxiosResponse> {
    return this.api.get(`${path}/list`, { params: query })
  }

  /**
   * โซน + แผงค้าแบบย่อสำหรับทำ dropdown — เบากว่า findAll มาก (ไม่ join qrCode/geometry/meter)
   *
   * **ไม่ reuse paramsFindAll** เพราะตัวนั้นบังคับ page/pageLimit และมีคน consume 20+ จุด
   * envelope ของเส้นนี้ยังไม่ยืนยันกับ backend (เส้น zone เดิมคืน array ดิบใน res.data
   * ส่วนโมดูลใหม่ห่อด้วย { data }) ผู้เรียกจึงต้อง normalize เอง — ดู loadZoneOptions
   * ใน pages/stallReclaim/useStallReclaimFilter.ts ที่มี fallback ไป findAll ให้ด้วย
   */
  static lookup(branchId?: number): Promise<AxiosResponse<any>> {
    return this.api.get(`${path}/lite`, { params: { view: "lookup", branchId } });
  }

  static findById(id: number): Promise<any> {
    return this.api.get(`${path}/${id}`)
  }

  static create(body: IZone): Promise<any> {
    return this.api.post(path, body).then((res) => res);
  }

  static update(id: number, body: IZone): Promise<any> {
    return this.api.patch(`${path}/${id}`, body).then((res) => res);
  }

  static remove(id: number): Promise<any> {
    return this.api.delete(`${path}/${id}`).then((res) => res);
  }
}
